import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import Logo from '../components/Logo';
import { postLoginPath } from '../utils/licenseRouting';
import { wasSessionExpired, clearSessionExpired } from '../utils/sessionRefresh';
import { canBuyInApp } from '../utils/native';

function LoginPage() {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // OFF-24: say why the user is back on the login page
  const [sessionExpired] = useState(wasSessionExpired);
  useEffect(() => { clearSessionExpired(); }, []);
  const navigate = useNavigate();
  const { login, user } = useAuth();

  useEffect(() => {
    // Check if remembered credentials exist
    const rememberedUsername = localStorage.getItem('rememberedUsername');
    if (rememberedUsername) {
      setUsername(rememberedUsername);
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    if (user) {
      navigate(postLoginPath(user));
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(username, password);
    
    if (result.success) {
      // Save username if "Remember Me" is checked
      if (rememberMe) {
        localStorage.setItem('rememberedUsername', username);
      } else {
        localStorage.removeItem('rememberedUsername');
      }
      // Licence-lapsed tenants: admins to checkout, others to the notice.
      navigate(postLoginPath(result.user));
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 px-4">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 fade-in">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Link to="/" aria-label={t('auth.loginPage.homeAria')} className="hover:opacity-90 transition">
              <Logo width={180} height={54} />
            </Link>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 italic mb-2">Reliable Unified Network for Value Yielding Automations</p>
          <p className="text-lg text-gray-700 dark:text-gray-300 font-medium">{t('auth.loginPage.tagline')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="username" className="form-label">
              {t('auth.loginPage.usernameLabel')}
            </label>
            <input
              id="username"
              type="text"
              data-testid="login-username-input"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition"
              placeholder={t('auth.loginPage.usernamePlaceholder')}
            />
          </div>

          <div>
            <label htmlFor="password" className="form-label">
              {t('auth.password')}
            </label>
            <input
              id="password"
              type="password"
              data-testid="login-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition"
              placeholder={t('auth.passwordPlaceholder')}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                type="checkbox"
                data-testid="login-remember-checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded cursor-pointer"
              />
              <label htmlFor="remember-me" className="ms-2 block text-sm text-gray-700 dark:text-gray-300 cursor-pointer">{t('auth.loginPage.rememberMe')}</label>
            </div>
            <a
              href="/forgot-password"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              {t('auth.loginPage.forgotPassword')}
            </a>
          </div>

          {sessionExpired && !error && (
            <div data-testid="login-session-expired" role="status" className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 text-yellow-800 dark:text-yellow-200 px-4 py-3 rounded-lg text-sm">
              {t('shared.http.sessionExpired')}
            </div>
          )}
          {error && (
            <div data-testid="login-error-message" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            data-testid="login-submit-button"
            disabled={loading}
            className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? t('auth.loginPage.loggingIn') : t('auth.login')}
          </button>
        </form>

        {canBuyInApp() && (
          <div className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
            {t('auth.loginPage.noAccount')}{' '}
            <a href="/signup" className="text-primary-600 hover:text-primary-700 font-semibold">
              {t('auth.loginPage.signupCta')}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

export default LoginPage;